import { useCallback } from "react";
import useSWR from "swr";
import useUser from "../context/userHook";
import useTokenHook from "../context/useTokenHook";
import useRevalidateOnAddItemCompleted from "../ui/useRevalidateOnAddItemCompleted";
import { acceptOffer, dismissOffer, fetchOfferStatus } from "../../utils/requests/offerRequest";

/**
 * The first-month-free offer's status. Fetched once per session, and again the
 * moment a sync finishes — that is the "first successful sync" the offer waits
 * for, so a brand-new account sees it without reloading.
 */
const useFreeMonthOffer = (enabled: boolean) => {
  const { userId } = useUser();
  const { token } = useTokenHook();

  const { data, mutate } = useSWR(
    enabled && token && userId ? `freeMonthOffer:${userId}` : null,
    () => fetchOfferStatus(token!),
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );

  const revalidate = useCallback(() => {
    if (enabled) mutate();
  }, [enabled, mutate]);

  useRevalidateOnAddItemCompleted(revalidate);

  const accept = useCallback(() => acceptOffer(token!), [token]);

  const dismiss = useCallback(async () => {
    try {
      mutate(await dismissOffer(token!), { revalidate: false });
    } catch {
      // Dismissing is best-effort; the popup closes either way.
    }
  }, [mutate, token]);

  return { status: data ?? null, accept, dismiss, refresh: mutate };
};

export default useFreeMonthOffer;
